import { createFileRoute } from '@tanstack/react-router'
import TimesheetReportsPageHeader from '../../Pages/TimesheetReportsPage/TimesheetReportsPageHeader'
import TimesheetReportsPage from '../../Pages/TimesheetReportsPage/TimesheetReportsPage'
import PageBody from '../../Pages/Layout/PageBody'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import { processTRPCData } from '../../Queries/fetchData'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'
import PermissionDenied from '../../Pages/PermissionDenied'
import { qf } from '../../Queries/queryFormatter'
import { TimesheetReportsPageV2 } from '@2/pages/timesheet-report/timesheet-report-page'
import {
    setOrganisationReport,
    setReportData,
} from '@2/pages/timesheet-report/time-report-store'
import { TimeReportColumns } from '../../reports/Time/TimeReportColumns'
import { trpc } from '../../system/trpc'
import { dateStringLookup } from '../../Components/Filters'
import { queryClient } from '../../App'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import { format } from 'date-fns'
import { PageBody as V2PageBody } from '@2/layout/page-body'

export const Route = createFileRoute('/_private/timesheets/report')({
    beforeLoad: ({ params, search }) => {
        if (!canViewRoute(SessionStore.user, 'timesheetReports', params))
            throw new Error('Permission Denied')
        const report = getCacheItemById('organisationReports', search.report)
        setLayoutDefaults({
            pageTitle: report?.name || 'Timesheet Report',
            subMenu: 'time',
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    pendingComponent: LoadingSpinner,
    loader: async ({ location }) => await loader(location.search),
    loaderDeps: ({ search: { report } }) => ({
        report,
    }),
    component: Wrapper,
})

const loader = async ({ report }) => {
    const reportId = report || SessionStore?.organisation?.defaultTimeReportId
    if (migratedToV2()) {
        const res = reportId
            ? await trpc.timeReport.getOrganisationReport.query({
                  reportId,
              })
            : null

        const organisationReport =
            res || SessionStore.organisation.defaultTimeReport
        let dateRange = [null, null]
        if (organisationReport.options?.dateRange) {
            dateRange =
                dateStringLookup[organisationReport.options?.dateRange]?.(
                    organisationReport.options?.fortnightType
                ) || organisationReport.options?.dateRange
        }
        const { columns, filters, groupBy, sortBy, options, name } =
            organisationReport
        setOrganisationReport(
            {
                columns,
                filters,
                groupBy,
                organisationId: SessionStore.organisationId,
                sortBy,
                dateRange,
                invoiceDateType: SessionStore.settings.reportInvoiceDateType,
                id: reportId,
                options,
                name,
            },
            { needsRefetch: false }
        )
        const cacheData = await queryClient.fetchQuery({
            queryKey: ['timeReportCache', reportId],
            queryFn: () =>
                trpc.timeReport.getReportsData.query({
                    organisationId: SessionStore.organisationId,
                    userId: SessionStore.user?.id,
                    columns: organisationReport?.columns.filter(
                        (c) =>
                            !TimeReportColumns(reportId)[c]?.permissions ||
                            TimeReportColumns(reportId)[c].permissions()
                    ),
                    filters: organisationReport?.filters.map((f) => {
                        if (
                            TimeReportColumns(reportId)[f.column]?.type ===
                            'date'
                        ) {
                            let value = dateStringLookup[f.value]?.() || f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) =>
                                    d ? format(d, 'yyyy-MM-dd') : null
                                )
                            } else {
                                value = value
                                    ? format(value, 'yyyy-MM-dd')
                                    : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else if (
                            TimeReportColumns(reportId)[f.column]?.type ===
                            'time'
                        ) {
                            let value = f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) => {
                                    d = new Date(d)
                                    return d && d.getTime()
                                        ? format(d, 'yyyy-MM-dd HH:mm')
                                        : null
                                })
                            } else {
                                value = new Date(value)
                                value =
                                    value && value.getTime()
                                        ? format(value, 'yyyy-MM-dd HH:mm')
                                        : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else {
                            return f
                        }
                    }),
                    sortBy: organisationReport?.sortBy || [],
                    groupBy: organisationReport?.groupBy || [],
                    dateRange,
                    invoiceDateType:
                        SessionStore.settings.reportInvoiceDateType,
                }),
            staleTime: 5 * 60 * 1000,
        })

        setReportData(cacheData)
    }
    const data = await trpc.legacy.getTimesheetReportPageData.query({
        reportId,
    })
    return processTRPCData(data)
}

function Wrapper() {
    if (migratedToV2()) {
        return (
            <V2PageBody>
                <TimesheetReportsPageV2 />
            </V2PageBody>
        )
    }
    return (
        <>
            <TimesheetReportsPageHeader
                {...Route.useParams()}
                {...Route.useSearch()}
            />
            <PageBody>
                <TimesheetReportsPage
                    {...Route.useParams()}
                    {...Route.useSearch()}
                />
            </PageBody>
        </>
    )
}
